import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-body font-semibold rounded-control transition-colors duration-sm ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        /* shadcn legacy variants — kept so other components that import Button still render */
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 rounded-md",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 rounded-md",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline",

        /* Karijeeva variants, Porsche style: solid, frosted, outlined, quiet. */
        primary:
          "bg-brand-gold text-white hover:bg-brand-gold-deep",
        secondary:
          "bg-transparent text-brand-ink border border-brand-ink/80 hover:bg-brand-ink/[0.06]",
        ghost:
          "bg-transparent text-brand-ink hover:bg-brand-parchment-soft",
        dark:
          "bg-brand-ink text-white hover:bg-brand-obsidian-soft",
      },
      size: {
        sm:  "h-9 px-4 text-sm [&_svg]:size-4",
        md:  "h-12 px-6 text-base [&_svg]:size-4",
        lg:  "h-14 px-8 text-base [&_svg]:size-5",
        icon:"h-12 w-12 [&_svg]:size-5",
        /* legacy */
        default: "h-9 px-4 py-2 text-sm [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      data-loading={loading ? "true" : undefined}
      {...props}>
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden />
          <span>{children}</span>
        </span>
      ) : children}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
